import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SecondaryButton } from "@financial-edge/shared";
import AuthAlert from "../components/auth/AuthAlert";
import AuthShell from "../components/auth/AuthShell";
import PrimaryButton from "../components/layout/PrimaryButton";
import { resolveBackendAccess, RESOLUTION_STATUSES } from "../utils/auth/accessResolution";
import { buildSupportReference, getAuthStatusCopy } from "../utils/auth/authStatusCatalog";
import { buildLaunchPath, buildLaunchSearch, getEffectiveLaunchContext, parseIntent } from "../utils/auth/intent";
import { buildBootstrapDestinationUrl, issuePortalBootstrap } from "../utils/auth/portalBootstrap";
import { getValidatedBrowserSession } from "../utils/session/browserSession";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

const APPLICATION_RETURN_TO = "/application";

function getApplicationPortal(resolution) {
    if (!resolution?.allowedPortals?.length) {
        return null;
    }

    return resolution.allowedPortals.find((portal) => portal.portalId === "application")
        || (resolution.preferredPortal?.portalId === "application" ? resolution.preferredPortal : null);
}

export default function AuthStatusPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const launchContext = getEffectiveLaunchContext(location.search);
    const intent = parseIntent(location.state?.intent || params.get("intent") || launchContext.intent);
    const reason = location.state?.reason || params.get("reason") || "access_denied";
    const code = location.state?.code || params.get("code") || null;
    const supportMessage = location.state?.supportMessage || null;
    const requestId = location.state?.requestId || params.get("requestId") || null;
    const accessContext = location.state?.accessContext || null;
    const verificationContext = location.state?.verificationContext || null;
    const launchSession = location.state?.launchSession || null;
    const rememberDevice = location.state?.rememberDevice === true;
    const [startApplicationError, setStartApplicationError] = useState("");
    const [startingApplication, setStartingApplication] = useState(false);
    const shouldOfferStartNewApplication = useMemo(() => {
        if (intent !== "client" || reason !== "account_not_found") {
            return false;
        }

        const applicationContext = accessContext?.sharedAccessContext?.application || null;
        return applicationContext?.status === "new_user" && !applicationContext?.latestApplication;
    }, [accessContext, intent, reason]);
    const shouldOfferInvestorAccessRequest = useMemo(() => {
        return intent === "investor" && ["account_not_found", "access_denied", "access_unconfirmed"].includes(reason);
    }, [intent, reason]);
    const copy = shouldOfferStartNewApplication
        ? {
            title: "You do not have an existing application",
            body: "cPortal access is only available once you already have an application on file.",
            nextStep: "Start a new application now and we will take you straight into the form.",
        }
        : shouldOfferInvestorAccessRequest
            ? {
                title: "iPortal access needs approval",
                body: "This iPortal sign-in is limited to approved investors.",
                nextStep: "Submit your investor details and the review team will confirm whether access can be enabled.",
            }
            : getAuthStatusCopy(intent, reason, supportMessage);
    const supportReference = buildSupportReference({ reason, intent, requestId });
    const intentLaunchContext = {
        ...launchContext,
        intent,
    };

    useAuthPageMeta(copy.title);

    const handleStartNewApplication = async () => {
        setStartApplicationError("");
        setStartingApplication(true);

        try {
            const session = launchSession || await getValidatedBrowserSession();
            const authUserId = session?.identity?.identityId || session?.session?.identityId || null;
            const resolution = await resolveBackendAccess({
                session,
                intent: "application",
                hasExplicitIntent: true,
                source: launchContext.source,
                returnTo: APPLICATION_RETURN_TO,
                verifiedPhone: verificationContext?.phone || null,
                verifiedEmail: verificationContext?.email || null,
                authUserId,
            });
            const applicationPortal = getApplicationPortal(resolution);

            if (resolution.status !== RESOLUTION_STATUSES.confirmed || !applicationPortal) {
                throw new Error("We couldn't confirm a secure handoff to the application form.");
            }

            const bootstrap = await issuePortalBootstrap({
                session,
                portal: applicationPortal,
                requestedIntent: "application",
                hasExplicitIntent: true,
                source: launchContext.source,
                returnTo: APPLICATION_RETURN_TO,
                rememberDevice,
                verifiedPhone: verificationContext?.phone || null,
                verifiedEmail: verificationContext?.email || null,
                authUserId,
            });
            const applicationDestinationUrl = new URL(
                APPLICATION_RETURN_TO,
                bootstrap.destinationUrl || applicationPortal.destinationUrl
            ).toString();

            window.location.assign(
                buildBootstrapDestinationUrl(
                    applicationDestinationUrl,
                    bootstrap.bootstrapCode,
                    APPLICATION_RETURN_TO,
                    "application"
                )
            );
        } catch (error) {
            setStartApplicationError(error?.message || "We couldn't start a new application right now.");
            setStartingApplication(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Secure access review"
            title={copy.title}
            description={copy.body}
            status={reason.includes("session") ? "warning" : "error"}
            align="center"
            footer={shouldOfferStartNewApplication ? null : (
                <div className="space-y-3 text-sm text-secondary text-center">
                    <p>If you contact support, quote the support reference so we can investigate without asking you to share sensitive account information.</p>
                    <div className="inline-flex rounded-full bg-surface px-4 py-2 font-semibold text-foreground">
                        Support reference: {supportReference}
                    </div>
                    {code ? (
                        <div className="inline-flex rounded-full bg-surface px-4 py-2 font-semibold text-foreground">
                            Error code: <span className="ml-2 font-mono">{code}</span>
                        </div>
                    ) : null}
                </div>
            )}
        >
            <div className="space-y-4">
                <AuthAlert tone="info" title="What to do next">
                    {copy.nextStep}
                </AuthAlert>

                {startApplicationError ? (
                    <AuthAlert tone="error" title="We couldn't open the application form" live="assertive">
                        {startApplicationError}
                    </AuthAlert>
                ) : null}

                <div className="flex flex-col gap-3">
                    {shouldOfferStartNewApplication ? (
                        <PrimaryButton onClick={handleStartNewApplication} disabled={startingApplication}>
                            {startingApplication ? "Opening application form" : "Start new application"}
                        </PrimaryButton>
                    ) : null}
                    {shouldOfferInvestorAccessRequest ? (
                        <PrimaryButton
                            onClick={() => navigate(`/investor-access-request${buildLaunchSearch({ ...launchContext, intent })}`, {
                                state: {
                                    intent,
                                    source: launchContext.source,
                                    returnTo: launchContext.returnTo,
                                    email: verificationContext?.email || "",
                                    accessDetails: accessContext || null,
                                },
                            })}
                        >
                            Request iPortal access
                        </PrimaryButton>
                    ) : null}
                    {reason === "recovery_required" ? (
                        <PrimaryButton
                            onClick={() => navigate(buildLaunchPath("/auth/recovery", intentLaunchContext), {
                                state: {
                                    intent,
                                    source: launchContext.source,
                                    returnTo: launchContext.returnTo,
                                },
                            })}
                        >
                            Start account recovery
                        </PrimaryButton>
                    ) : null}
                    {!shouldOfferStartNewApplication && !shouldOfferInvestorAccessRequest ? (
                        <PrimaryButton
                            onClick={() => navigate(`/entry${buildLaunchSearch({ source: launchContext.source, returnTo: launchContext.returnTo, brokerageId: launchContext.brokerageId, intent })}`)}
                        >
                            Choose a workspace
                        </PrimaryButton>
                    ) : null}
                    <SecondaryButton
                        onClick={() => navigate(buildLaunchPath("/login", intentLaunchContext))}
                        className="text-lg"
                    >
                        Return to sign-in
                    </SecondaryButton>
                </div>
            </div>
        </AuthShell>
    );
}
